import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  NotificationsActive as NotificationsIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../../services/api';

const maintenanceTypes = [
  'refolding',
  'inspection',
  'revision',
  'calibration',
  'lineCheck',
  'porosityTest',
  'custom'
];

const ScheduleMaintenancePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [equipment, setEquipment] = useState<any>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [formData, setFormData] = useState({
    maintenance_type: '',
    description: '',
    due_date: null as Date | null,
    is_recurring: false,
    recurrence_interval_days: 365,
    reminder_days_before: [30, 14, 7, 1],
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [equipmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const serviceUserData = localStorage.getItem('serviceUserData');
      if (!serviceUserData) {
        navigate('/app/reminders/dashboard');
        return;
      }
      
      const userData = JSON.parse(serviceUserData);
      const serviceUserId = userData.id;
      const email = userData.email;

      const [subRes, equipRes] = await Promise.all([
        api.get(`/reminders/subscription/${serviceUserId}`),
        api.get(`/reminders/equipment?email=${email}`),
      ]);

      setSubscription(subRes.data);
      
      // Find the specific equipment
      const equip = equipRes.data.find((e: any) => e.id === equipmentId);
      if (equip) {
        setEquipment(equip);
      } else {
        setError(t('reminders.errors.equipmentNotFound'));
      }
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(t('reminders.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      maintenance_type: event.target.value,
    });
  };

  const handleDateChange = (value: any) => {
    const date = value instanceof Date ? value : value?.toDate ? value.toDate() : null;
    setFormData({
      ...formData,
      due_date: date,
    });
  };

  const handleRecurringChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      is_recurring: event.target.checked,
    });
  };

  const toggleReminderDay = (day: number) => {
    const days = [...formData.reminder_days_before];
    const index = days.indexOf(day);
    if (index > -1) {
      days.splice(index, 1);
    } else {
      days.push(day);
      days.sort((a, b) => b - a);
    }
    setFormData({
      ...formData,
      reminder_days_before: days,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.maintenance_type || !formData.due_date) {
      setError(t('common.fillRequiredFields'));
      return;
    }

    if (!subscription) {
      setError(t('reminders.errors.subscriptionNotFound'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const requestData: any = {
        equipment_id: equipmentId,
        maintenance_type: formData.maintenance_type,
        description: formData.description || `${formData.maintenance_type} for ${equipment?.brand} ${equipment?.model}`,
        due_date: formData.due_date.toISOString().split('T')[0],
        is_recurring: formData.is_recurring,
        reminder_days_before: formData.reminder_days_before,
        notes: formData.notes,
      };

      if (formData.is_recurring && formData.recurrence_interval_days) {
        requestData.recurrence_interval_days = formData.recurrence_interval_days;
      }

      await api.post('/reminders/schedules', requestData);
      navigate(-1);
    } catch (err: any) {
      console.error('Error creating schedule:', err);
      setError(t('reminders.errors.scheduleFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('reminders.maintenance.schedule')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 10, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ScheduleIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4">
                {t('reminders.maintenance.schedule')}
              </Typography>
              {equipment && (
                <Typography variant="subtitle1" color="text.secondary">
                  {equipment.brand} {equipment.model} - {equipment.equipment_type?.name}
                </Typography>
              )}
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>{t('reminders.maintenance.type')}</InputLabel>
                <Select
                  value={formData.maintenance_type}
                  onChange={handleSelectChange}
                  label={t('reminders.maintenance.type')}
                >
                  {maintenanceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`reminders.maintenance.types.${type}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('reminders.maintenance.description')}
                value={formData.description}
                onChange={handleInputChange('description')}
                placeholder={`${formData.maintenance_type} for ${equipment?.brand} ${equipment?.model}`}
              />

              <DatePicker
                label={t('reminders.maintenance.dueDate')}
                value={formData.due_date}
                onChange={handleDateChange}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_recurring}
                    onChange={handleRecurringChange}
                  />
                }
                label={t('reminders.maintenance.recurring')}
              />

              {formData.is_recurring && (
                <TextField
                  fullWidth
                  type="number"
                  label={t('reminders.maintenance.interval')}
                  value={formData.recurrence_interval_days}
                  onChange={handleInputChange('recurrence_interval_days')}
                  InputProps={{
                    inputProps: { min: 1, max: 1825 }
                  }}
                  helperText={t('reminders.maintenance.intervalHelper')}
                />
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotificationsIcon fontSize="small" />
                  {t('reminders.maintenance.reminderDays')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {[90, 60, 30, 14, 7, 3, 1].map((day) => (
                    <Chip
                      key={day}
                      label={`${day} ${t('common.days')}`}
                      onClick={() => toggleReminderDay(day)}
                      color={formData.reminder_days_before.includes(day) ? 'primary' : 'default'}
                      variant={formData.reminder_days_before.includes(day) ? 'filled' : 'outlined'}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('reminders.maintenance.notes')}
                value={formData.notes}
                onChange={handleInputChange('notes')}
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  sx={{ minWidth: 150 }}
                >
                  {saving ? <CircularProgress size={24} /> : t('reminders.maintenance.schedule')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(-1)}
                  disabled={saving}
                >
                  {t('common.cancel')}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default ScheduleMaintenancePage;